/**
 * Display helpers shared by the Service Review tab and the Project Review
 * pages. Both report the same phenomenon from different angles, so a colour
 * or a sentence has to mean the same thing on either one.
 */

import { getFormattedDate } from "@utils/helperFunctions";

export type AccuracyDirection = "none" | "over" | "under";

// `over` costs margin - the work took longer than it was sold for - so it
// carries the heavier colour. `under` loses deals on price, which is real but
// recoverable. `none` is a finding rather than a warning and stays neutral.
export const DIRECTION_COLOR: Record<AccuracyDirection, string | undefined> = {
  over: "#B90200",
  under: "#FCCE2B",
  none: undefined,
};

/** Direction is carried by the sentence and the colour, so the sign is dropped. */
export const asPercent = (ratio?: number | null) =>
  ratio === undefined || ratio === null
    ? ""
    : `${Math.abs(Math.round(ratio * 100))}%`;

/**
 * Formats a bare `YYYY-MM-DD` from the API in the app's MM/DD/YYYY style.
 *
 * `getFormattedDate` in @utils/helperFunctions needs a Date, not a string --
 * handing it the string throws `date.getFullYear is not a function`. And the
 * obvious `new Date(iso)` parses as UTC midnight while `getDate()` reads local,
 * so every date renders a day early anywhere west of UTC. Appending the time
 * forces a local-midnight parse, which is what makes this correct.
 */
export const formatDeliveryDate = (iso?: string | null) =>
  iso ? getFormattedDate(new Date(`${iso}T00:00:00`)) : "";

/**
 * "Actual hours run 35% over estimates" rather than "Runs 35% over": the row
 * compares two quantities, and naming both says which way round it goes.
 */
export const biasSentence = (
  direction: AccuracyDirection,
  biasPercentage?: number | null
) =>
  direction === "none"
    ? "No consistent bias"
    : `Actual hours run ${asPercent(biasPercentage)} ${direction} estimates`;

/** Hours are always rendered rounded to a whole number. */
export const hours = (value: number) => `${Math.round(value)}`;

/**
 * A dash, never "0%": null means nothing settled carried an estimate, which
 * is not the same as an on-budget 0% variance.
 */
export const variancePercent = (value?: number | null) =>
  value === null || value === undefined ? "—" : asPercent(value);

/**
 * Signed, because the sign is the finding: +21 ran over what was sold, -21
 * came in under it. Colour follows the same rule as the service review, so
 * red means one thing across every surface that renders it.
 */
export const delta = (value: number) => {
  const rounded = Math.round(value);
  const color =
    rounded > 0
      ? DIRECTION_COLOR.over
      : rounded < 0
      ? DIRECTION_COLOR.under
      : undefined;

  return (
    <span style={color ? { color } : undefined}>
      {rounded > 0 ? `+${rounded}` : `${rounded}`}
    </span>
  );
};
